import { Routes } from "@/shared/types/enums/Routes.enum";

/**
 * Tipos de flujos de registro disponibles
 */
export enum RegistrationFlowType {
    PF_CIUDADANO = 'pf_ciudadano',
    PF_ABOGADO = 'pf_abogado',
    PJ_JURIDICA = 'pj_juridica',
    PJ_GUBERNAMENTAL = 'pj_gubernamental',
}

/**
 * Interfaz para definir un paso en el flujo de registro
 */
export interface RegistrationStep {
    /** Nombre identificador del paso */
    step: string;
    /** Ruta de la página */
    route: Routes;
    /** Campos requeridos que deben existir en localStorage para este paso */
    requiredFields: string[];
    /** Orden del paso (1-based index) */
    order: number;
}

/**
 * Interfaz para definir un flujo completo de registro
 */
export interface RegistrationFlow {
    /** Tipo de flujo */
    type: RegistrationFlowType;
    /** Lista de pasos ordenados */
    steps: RegistrationStep[];
}

/**
 * Configuración del flujo de registro para Persona Física - Ciudadano
 * Pasos: Identificación -> Credenciales -> Términos
 */
const PF_CIUDADANO_FLOW: RegistrationFlow = {
    type: RegistrationFlowType.PF_CIUDADANO,
    steps: [
        {
            step: 'identificacion',
            route: Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_IDENTIFICACION,
            requiredFields: [],
            order: 1,
        },
        {
            step: 'credenciales',
            route: Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_CREDENCIALES,
            requiredFields: ['identificacion', 'correo', 'telefono'],
            order: 2,
        },
        {
            step: 'terminos',
            route: Routes.REGISTRO_PERSONA_FISICA_CIUDADANO_TERMINOS,
            requiredFields: ['identificacion', 'correo', 'telefono', 'clave'],
            order: 3,
        },
    ],
};

/**
 * Configuración del flujo de registro para Persona Física - Abogado
 * Pasos: Identificación -> Datos Profesionales -> Credenciales -> Términos
 */
const PF_ABOGADO_FLOW: RegistrationFlow = {
    type: RegistrationFlowType.PF_ABOGADO,
    steps: [
        {
            step: 'identificacion',
            route: Routes.REGISTRO_PERSONA_FISICA_ABOGADO_IDENTIFICACION,
            requiredFields: [],
            order: 1,
        },
        {
            step: 'datos_profesionales',
            route: Routes.REGISTRO_PERSONA_FISICA_ABOGADO_DATOS_PROFESIONALES,
            requiredFields: ['identificacion', 'correo', 'telefono', 'matricula'],
            order: 2,
        },
        {
            step: 'credenciales',
            route: Routes.REGISTRO_PERSONA_FISICA_ABOGADO_CREDENCIALES,
            requiredFields: ['identificacion', 'correo', 'telefono', 'matricula', 'fechaEmisionCarnet'],
            order: 3,
        },
        {
            step: 'terminos',
            route: Routes.REGISTRO_PERSONA_FISICA_ABOGADO_TERMINOS,
            requiredFields: ['identificacion', 'correo', 'telefono', 'matricula', 'fechaEmisionCarnet', 'clave'],
            order: 4,
        },
    ],
};

/**
 * Configuración del flujo de registro para Persona Jurídica - Jurídica
 * Pasos: Identificación -> Datos -> Documentos -> Contactos -> Términos
 */
const PJ_JURIDICA_FLOW: RegistrationFlow = {
    type: RegistrationFlowType.PJ_JURIDICA,
    steps: [
        {
            step: 'identificacion',
            route: Routes.REGISTRO_PERSONA_JURIDICA_IDENTIFICACION,
            requiredFields: [],
            order: 1,
        },
        {
            step: 'datos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_DATOS,
            // Para acceder a datos, solo necesita idUsuario (del paso anterior)
            requiredFields: ['idUsuario'],
            order: 2,
        },
        {
            step: 'documentos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_DOCUMENTOS,
            // Para acceder a documentos, necesita haber completado datos (rnc y logoNombre)
            requiredFields: ['idUsuario', 'rnc', 'logoNombre'],
            order: 3,
        },
        {
            step: 'contactos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_CONTACTOS,
            // Solo validar metadatos - los archivos están en memoria del Context
            requiredFields: ['idUsuario', 'rnc', 'logoNombre', 'documentoRepresentacionNombre', 'documentoRegistroNombre'],
            order: 4,
        },
        {
            step: 'terminos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_TERMINOS,
            // Solo validar metadatos - validación estricta de archivos se hace en el submit
            requiredFields: ['idUsuario', 'rnc', 'logoNombre', 'documentoRepresentacionNombre', 'documentoRegistroNombre', 'correo', 'telefono'],
            order: 5,
        },
    ],
};

/**
 * Configuración del flujo de registro para Persona Jurídica - Gubernamental
 * Pasos: Identificación -> Datos -> Documentos -> Contactos -> Términos
 */
const PJ_GUBERNAMENTAL_FLOW: RegistrationFlow = {
    type: RegistrationFlowType.PJ_GUBERNAMENTAL,
    steps: [
        {
            step: 'identificacion',
            route: Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_IDENTIFICACION,
            requiredFields: [],
            order: 1,
        },
        {
            step: 'datos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_DATOS,
            requiredFields: ['idUsuario'],
            order: 2,
        },
        {
            step: 'documentos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_DOCUMENTOS,
            // Solo validar metadatos - los archivos están en memoria del Context
            requiredFields: ['idUsuario', 'rnc', 'logoNombre'],
            order: 3,
        },
        {
            step: 'contactos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_CONTACTOS,
            // Solo validar metadatos - los archivos están en memoria del Context
            requiredFields: ['idUsuario', 'rnc', 'logoNombre', 'documentoRepresentacionNombre', 'documentoRegistroNombre'],
            order: 4,
        },
        {
            step: 'terminos',
            route: Routes.REGISTRO_PERSONA_JURIDICA_GUBERNAMENTAL_TERMINOS,
            // Solo validar metadatos - validación estricta de archivos se hace en el submit
            requiredFields: ['idUsuario', 'rnc', 'logoNombre', 'documentoRepresentacionNombre', 'documentoRegistroNombre', 'correo', 'telefono'],
            order: 5,
        },
    ],
};

/**
 * Mapa de todos los flujos de registro disponibles
 */
export const REGISTRATION_FLOWS: Record<RegistrationFlowType, RegistrationFlow> = {
    [RegistrationFlowType.PF_CIUDADANO]: PF_CIUDADANO_FLOW,
    [RegistrationFlowType.PF_ABOGADO]: PF_ABOGADO_FLOW,
    [RegistrationFlowType.PJ_JURIDICA]: PJ_JURIDICA_FLOW,
    [RegistrationFlowType.PJ_GUBERNAMENTAL]: PJ_GUBERNAMENTAL_FLOW,
};

/**
 * Obtiene el flujo de registro según la ruta actual
 * @param currentRoute - Ruta actual de la aplicación
 * @returns El flujo correspondiente o undefined si no se encuentra
 */
export const getFlowByRoute = (currentRoute: string): RegistrationFlow | undefined => {
    // Normalizar la ruta
    const normalizedRoute = currentRoute.toLowerCase();
    
    // Determinar el flujo basado en la ruta
    if (normalizedRoute.includes('/pf/ciudadano')) {
        return REGISTRATION_FLOWS[RegistrationFlowType.PF_CIUDADANO];
    }
    
    if (normalizedRoute.includes('/pf/abogado')) {
        return REGISTRATION_FLOWS[RegistrationFlowType.PF_ABOGADO];
    }
    
    if (normalizedRoute.includes('/pj/juridica')) {
        return REGISTRATION_FLOWS[RegistrationFlowType.PJ_JURIDICA];
    }
    
    if (normalizedRoute.includes('/pj/gubernamental')) {
        return REGISTRATION_FLOWS[RegistrationFlowType.PJ_GUBERNAMENTAL];
    }
    
    return undefined;
};

/**
 * Obtiene el paso actual dentro de un flujo basado en la ruta
 * @param flow - Flujo de registro
 * @param currentRoute - Ruta actual
 * @returns El paso actual o undefined si no se encuentra
 */
export const getCurrentStep = (flow: RegistrationFlow, currentRoute: string): RegistrationStep | undefined => {
    return flow.steps.find(step => step.route === currentRoute);
};
